import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  BENEFIT_VIOLATING_FIELD_OPTIONS,
  RejectBenefitServiceBody,
} from "../../../entities/admin-benefits";

interface BenefitsRejectDialogProps {
  open: boolean;
  serviceTitle?: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (body: RejectBenefitServiceBody) => void;
}

export const BenefitsRejectDialog = ({
  open,
  serviceTitle,
  isSubmitting,
  onClose,
  onSubmit,
}: BenefitsRejectDialogProps) => {
  const [comments, setComments] = useState<string[]>([""]);
  const [violatingFields, setViolatingFields] = useState<string[]>([]);

  const reset = () => {
    setComments([""]);
    setViolatingFields([]);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = () => {
    const moderationComments = comments
      .map((c) => c.trim())
      .filter(Boolean);
    if (moderationComments.length === 0) {
      alert("Добавьте хотя бы одно замечание");
      return;
    }
    onSubmit({
      moderationComments,
      violatingFields: violatingFields.length > 0 ? violatingFields : undefined,
    });
  };

  const toggleField = (key: string) => {
    setViolatingFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key],
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Отклонить услугу</DialogTitle>
      <DialogContent>
        {serviceTitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {serviceTitle}
          </Typography>
        )}
        <Typography variant="subtitle2" gutterBottom>
          Замечания модератора *
        </Typography>
        {comments.map((comment, index) => (
          <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={comment}
              onChange={(e) => {
                const next = [...comments];
                next[index] = e.target.value;
                setComments(next);
              }}
              placeholder="Текст замечания"
            />
            {comments.length > 1 && (
              <IconButton
                color="error"
                onClick={() =>
                  setComments(comments.filter((_, i) => i !== index))
                }
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setComments([...comments, ""])}
          sx={{ mb: 2 }}
        >
          Добавить замечание
        </Button>

        <Typography variant="subtitle2" gutterBottom>
          Поля для подсветки в приложении
        </Typography>
        <FormGroup>
          {BENEFIT_VIOLATING_FIELD_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.key}
              control={
                <Checkbox
                  checked={violatingFields.includes(option.key)}
                  onChange={() => toggleField(option.key)}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Отмена
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          Отклонить
        </Button>
      </DialogActions>
    </Dialog>
  );
};
